import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Hand, Truck, Wallet, ReceiptText, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { api, errorMessage } from "../../lib/api";
import { nok, orderUrl } from "../../lib/shop";
import { useLang } from "../../context/LanguageContext";

// «Vel-rad» for RadioGroup (design: motion_and_microinteractions.radio_rows)
export const OptionRow = ({ value, selected, Icon, title, subtitle, right, testId, disabled }) => (
  <label
    htmlFor={`opt-${value}`}
    className={`flex items-center gap-3 rounded-lg border p-3 min-h-11 cursor-pointer transition-colors duration-150 ${selected ? "border-[hsl(var(--brand))]/30 bg-[hsl(var(--accent))]" : "border-border hover:bg-accent"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    data-testid={testId}
    data-selected={selected ? "true" : "false"}
  >
    <RadioGroupItem value={value} id={`opt-${value}`} disabled={disabled} />
    {Icon && <Icon className="w-4 h-4 text-muted-foreground shrink-0" />}
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium leading-snug">{title}</span>
      {subtitle && <span className="block text-xs text-muted-foreground leading-snug mt-0.5">{subtitle}</span>}
    </span>
    {right && <span className="text-sm font-medium tabular-nums shrink-0">{right}</span>}
  </label>
);

/**
 * Kasse-skjema for éi bestilling hos éin arrangør: levering (henting/sending), betaling (Vipps/faktura), kjøparinfo, adresse ved sending,
 * melding og sum. `items` = korglinjer [{product_id, name, price, quantity}], `shop` = offentleg butikk-header (organizer, can_sell, pickup_enabled, …).
 * Betalinga går direkte til arrangøren – Right in formidlar berre bestillinga. `onSubmitted(order)` vert kalla rett før redirect/navigasjon (t.d. tøm korga).
 */
export const CheckoutForm = ({ items, shop, onSubmitted, className = "" }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const org = shop.organizer || {};
  const canSell = !!shop.can_sell && items.length > 0;

  const deliveryOptions = useMemo(() => {
    const out = [];
    if (shop.pickup_enabled) out.push("pickup");
    if (shop.shipping_enabled) out.push("shipping");
    return out.length ? out : ["pickup"];
  }, [shop.pickup_enabled, shop.shipping_enabled]);
  const paymentOptions = useMemo(() => {
    const out = [];
    if (shop.vipps_available) out.push("vipps");
    if (shop.invoice_available) out.push("invoice");
    return out;
  }, [shop.vipps_available, shop.invoice_available]);

  const [delivery, setDelivery] = useState(deliveryOptions[0]);
  const [payment, setPayment] = useState(paymentOptions[0] || "vipps");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address_line1: "", postal_code: "", city: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const shippingPrice = delivery === "shipping" ? Number(shop.shipping_price || 0) : 0;
  const total = subtotal + shippingPrice;
  const isFree = total === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSell || submitting) return;
    if (form.name.trim().length < 2 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast.error(t("Fyll inn navn og e-post"));
      return;
    }
    if (delivery === "shipping" && (form.address_line1.trim().length < 3 || form.postal_code.replace(/\D/g, "").length !== 4 || form.city.trim().length < 2)) {
      toast.error(t("Fyll inn adresse, postnummer og poststed"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/shop/orders", {
        organizer_id: org.id,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        delivery_method: delivery,
        address_line1: form.address_line1.trim(),
        postal_code: form.postal_code.replace(/\D/g, ""),
        city: form.city.trim(),
        payment_method: payment,
        note: form.note.trim(),
      });
      onSubmitted?.(data.order);
      if (data.payment_redirect_url) {
        window.location.assign(data.payment_redirect_url);
        return;
      }
      navigate(orderUrl(data.order.id));
    } catch (err) {
      toast.error(errorMessage(err));
      setSubmitting(false);
    }
  };

  const ctaLabel = submitting
    ? payment === "vipps" && !isFree ? t("Sender deg til Vipps …") : t("Bestiller …")
    : isFree ? t("Bestill") : payment === "invoice" ? t("Bestill med faktura – {amount}", { amount: nok(total) }) : t("Kjøp nå – {amount}", { amount: nok(total) });

  if (!shop.can_sell) {
    return <div className="rounded-xl bg-secondary p-6 text-center text-sm text-muted-foreground" data-testid="checkout-closed">{t("Butikken tar ikke imot bestillinger akkurat nå.")}</div>;
  }

  return (
    <form onSubmit={submit} className={`space-y-5 ${className}`} data-testid="checkout-form">
      {/* Levering */}
      <div className="space-y-2">
        <Label>{t("Levering")}</Label>
        <RadioGroup value={delivery} onValueChange={setDelivery} className="gap-2" data-testid="checkout-delivery-method-radio">
          {deliveryOptions.includes("pickup") && (
            <OptionRow value="pickup" selected={delivery === "pickup"} Icon={Hand} title={t("Henting hos arrangøren")} subtitle={shop.pickup_place || t("Hentes etter avtale hos arrangøren")} right={t("Gratis")} testId="checkout-delivery-pickup" />
          )}
          {deliveryOptions.includes("shipping") && (
            <OptionRow value="shipping" selected={delivery === "shipping"} Icon={Truck} title={t("Sending i posten")} subtitle={t("Sendes til adressen du oppgir")} right={Number(shop.shipping_price || 0) === 0 ? t("Gratis frakt") : `+ ${nok(shop.shipping_price)}`} testId="checkout-delivery-shipping" />
          )}
        </RadioGroup>
      </div>

      {/* Betaling */}
      {!isFree && (
        <div className="space-y-2">
          <Label>{t("Betaling")}</Label>
          <RadioGroup value={payment} onValueChange={setPayment} className="gap-2" data-testid="checkout-payment-method-radio">
            {paymentOptions.includes("vipps") && (
              <OptionRow value="vipps" selected={payment === "vipps"} Icon={Wallet} title={t("Vipps")} subtitle={t("Du betaler i Vipps-appen nå")} testId="checkout-payment-vipps" />
            )}
            {paymentOptions.includes("invoice") && (
              <OptionRow value="invoice" selected={payment === "invoice"} Icon={ReceiptText} title={t("Faktura")} subtitle={t("Du får faktura (PDF) på e-post – forfall {days} dager", { days: shop.invoice_due_days || 14 })} testId="checkout-payment-invoice" />
            )}
          </RadioGroup>
        </div>
      )}

      {/* Kjøpar */}
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="co-name">{t("Navn")}</Label>
          <Input id="co-name" value={form.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" maxLength={120} required className="h-11" data-testid="checkout-buyer-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="co-email">{t("E-post")}</Label>
          <Input id="co-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" maxLength={200} required className="h-11" data-testid="checkout-buyer-email-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="co-phone">{t("Telefon (valgfritt)")}</Label>
          <Input id="co-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" inputMode="tel" maxLength={30} className="h-11" data-testid="checkout-buyer-phone-input" />
        </div>
        {delivery === "shipping" && (
          <div className="space-y-3 rounded-lg border border-border p-3" data-testid="checkout-shipping-address">
            <div className="space-y-1.5">
              <Label htmlFor="co-addr">{t("Adresse")}</Label>
              <Input id="co-addr" value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} autoComplete="street-address" maxLength={200} required className="h-11" data-testid="checkout-shipping-address-input" />
            </div>
            <div className="grid grid-cols-5 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="co-postal">{t("Postnummer")}</Label>
                <Input id="co-postal" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, "").slice(0, 4))} autoComplete="postal-code" inputMode="numeric" maxLength={4} required className="h-11 tabular-nums" data-testid="checkout-shipping-postalcode-input" />
              </div>
              <div className="space-y-1.5 col-span-3">
                <Label htmlFor="co-city">{t("Poststed")}</Label>
                <Input id="co-city" value={form.city} onChange={(e) => set("city", e.target.value)} autoComplete="address-level2" maxLength={100} required className="h-11" data-testid="checkout-shipping-city-input" />
              </div>
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="co-note">{t("Melding til selger (valgfritt)")}</Label>
          <Textarea id="co-note" value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} maxLength={500} data-testid="checkout-note-textarea" />
        </div>
      </div>

      {/* Sum */}
      <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1" data-testid="checkout-summary">
        {items.map((i) => (
          <div key={i.product_id} className="flex justify-between gap-3"><span className="text-muted-foreground truncate">{i.name} × {i.quantity}</span><span className="tabular-nums shrink-0">{nok(i.price * i.quantity)}</span></div>
        ))}
        {delivery === "shipping" && <div className="flex justify-between gap-3"><span className="text-muted-foreground">{t("Frakt")}</span><span className="tabular-nums">{nok(shippingPrice)}</span></div>}
        <div className="flex justify-between gap-3 font-semibold pt-1 border-t border-border"><span>{t("Totalt")}</span><span className="tabular-nums" data-testid="checkout-summary-total">{nok(total)}</span></div>
      </div>

      <Button type="submit" className="w-full h-12 text-base" disabled={submitting || !canSell || (!isFree && paymentOptions.length === 0)} data-testid={payment === "invoice" && !isFree ? "checkout-submit-invoice-button" : "checkout-submit-vipps-button"}>
        {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ShoppingBag className="w-5 h-5 mr-2" />}
        {ctaLabel}
      </Button>
      <p className="text-xs text-muted-foreground leading-relaxed" data-testid="checkout-terms-note">
        {t("Kjøpet er bindende. Selger er {org} (org.nr. {orgnr}) – betalingen går direkte til selgeren.", { org: org.org_name || "", orgnr: org.org_number || "–" })}{" "}
        <Link to="/vilkar" className="underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="checkout-terms-link">{t("Vilkår for kjøp")}</Link>
        {" · "}{t("Du får ordrebekreftelse på e-post.")}
      </p>
    </form>
  );
};
